import { useEffect, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Breadcrumbs,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Link
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { exportDailyReports, fetchDailyReports } from '../store/slices/dailyReportsSlice';

const columns = [
  { id: 'date', label: 'Date', sortable: true },
  { id: 'totalTransactions', label: 'Total Transaction', align: 'right', sortable: true },
  { id: 'totalTickets', label: 'Total Ticket', align: 'right', sortable: true },
  { id: 'totalFare', label: 'Total Fare', align: 'right', sortable: true }
];

const formatDisplayDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('en-GB');
};

const DailyReportPage = () => {
  const dispatch = useDispatch();
  const { items, meta, loading, error, query, exportStatus, exportError, isMockData } =
    useSelector((state) => state.dailyReports);

  const [startDateTime, setStartDateTime] = useState(query.startDateTime);
  const [endDateTime, setEndDateTime] = useState(query.endDateTime);
  const [page, setPage] = useState(Math.max((query.page ?? 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query.pageSize ?? 30);
  const [sortBy, setSortBy] = useState(query.sortBy);
  const [sortDirection, setSortDirection] = useState(query.sortDirection);
  const [dense, setDense] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isDateRangeInvalid =
    startDateTime && endDateTime && new Date(startDateTime) > new Date(endDateTime);

  useEffect(() => {
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      fetchDailyReports({
        page: page + 1,
        pageSize: rowsPerPage,
        startDateTime,
        endDateTime,
        sortBy,
        sortDirection
      })
    );
  }, [dispatch, page, rowsPerPage, startDateTime, endDateTime, sortBy, sortDirection, isDateRangeInvalid]);

  useEffect(() => {
    if (typeof query.page === 'number') {
      const zeroBasedPage = Math.max(query.page - 1, 0);
      setPage((prev) => (prev === zeroBasedPage ? prev : zeroBasedPage));
    }
    if (typeof query.pageSize === 'number') {
      setRowsPerPage((prev) => (prev === query.pageSize ? prev : query.pageSize));
    }
  }, [query.page, query.pageSize]);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleExport = () => {
    setMenuAnchor(null);
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      exportDailyReports({
        startDateTime,
        endDateTime,
        sortBy,
        sortDirection
      })
    );
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Daily Report</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Daily Report
          </Typography>
          <Typography color="text.secondary">
            Review daily aggregates for transactions and issued tickets.
          </Typography>
        </Box>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 6,
            sx: {
              boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background:
                'radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff',
              borderRadius: 1,
              mt: 1
            }
          }}
        >
          <MenuItem onClick={handleExport} disabled={exportStatus === 'loading'}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
          </MenuItem>
        </Menu>
      </Stack>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ lg: 'center' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', lg: 'auto' }, flexShrink: 0 }}
            >
              <TextField
                type="datetime-local"
                label="Start Date + Time"
                value={startDateTime}
                onChange={(event) => {
                  setPage(0);
                  setStartDateTime(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
              <TextField
                type="datetime-local"
                label="End Date + Time"
                value={endDateTime}
                onChange={(event) => {
                  setPage(0);
                  setEndDateTime(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 250 } }}
              />
            </Stack>
          </Stack>

          {isDateRangeInvalid && (
            <Alert severity="error">End datetime must be on or after start datetime.</Alert>
          )}
          {isMockData && !error && (
            <Alert severity="info">
              API endpoint is not available yet in this environment. Showing fallback dummy data
              using the final API shape.
            </Alert>
          )}
          {error && !loading && <Alert severity="error">{error}</Alert>}
          {exportError && <Alert severity="error">{exportError}</Alert>}

          {loading && <LinearProgress />}

          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <TableContainer>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sortDirection={sortBy === column.id ? sortDirection : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(15, 23, 42, 0.03)'
                        }
                      }}
                    >
                      <TableCell>{formatDisplayDate(row.date)}</TableCell>
                      <TableCell align="right">{row.totalTransactions.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.totalTickets.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.totalFare.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {!items.length && !loading && !isDateRangeInvalid && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            No daily reports found
                          </Typography>
                          <Typography color="text.secondary">
                            Adjust the datetime range to view additional records.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <FormControlLabel
              control={<Switch checked={dense} onChange={(event) => setDense(event.target.checked)} />}
              label="Dense"
            />

            <TablePagination
              component="div"
              count={meta?.total_items ?? 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 30, 50]}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onRowsPerPageChange={(event) => {
                const nextRows = parseInt(event.target.value, 10);
                setRowsPerPage(nextRows);
                setPage(0);
              }}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 0, sm: 2 }
                }
              }}
            />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default DailyReportPage;
